import { Fragment, FunctionComponent, useContext, useEffect, useState } from 'react';
import ErrorMessage from '../../../common/error/ErrorMessage';
import { tryFormatDateTime } from '../../../common/utils/dateUtils';
import { ConfigContext } from '../../configuration/ConfigContext';
import { usePropertyAssociationContext } from '../../propertyAssociation/PropertyAssociationContext';
import Pagination from '../../shared/Pagination';
import { Table, TableHead, TableHeader, TableRow } from '../../shared/Table';
import { usePagination } from '../../shared/usePagination';
import { UserAdvert } from '../models/UserAdvert';
import { getUserAdverts } from '../userAdvertService';

type Props = {
    deletedAdverts: string[];
    selectAdvertId: (advertId: string) => void;
};

const ListActiveUserAdverts: FunctionComponent<Props> = ({ deletedAdverts, selectAdvertId }) => {
    const { apiBaseUrl } = useContext(ConfigContext);
    const { currentPropertyAssociationId, currentArea } = usePropertyAssociationContext();
    const [adverts, setAdverts] = useState<UserAdvert[] | undefined>();
    const [error, setError] = useState<string | undefined>();
    const { currentPage, setCurrentPage, currentItems, totalPages, itemsPerPage } = usePagination(adverts ?? []);

    useEffect(() => {
        getUserAdverts(
            apiBaseUrl,
            currentPropertyAssociationId,
            currentArea?.propertyAssociationAreaId,
            ({ adverts }) => setAdverts(adverts),
            () => setError('Kunde inte ladda anslagstavla'),
        );
    }, []);

    useEffect(() => {
        if (adverts) {
            setAdverts(adverts.filter((advert) => !deletedAdverts.includes(advert.advertId)));
        }
    }, [deletedAdverts]);

    if (error) {
        return <ErrorMessage message={error} />;
    }

    if (!adverts) {
        return null;
    }

    if (adverts.length === 0) {
        return (
            <p>
                <i>Inga aktiva annonser</i>
            </p>
        );
    }

    return (
        <Fragment>
            <Table withPagination={totalPages > 1}>
                <TableHead>
                    <tr>
                        <TableHeader>Rubrik</TableHeader>
                        <TableHeader>Skapad den</TableHeader>
                        <TableHeader className="text-end">Användare</TableHeader>
                    </tr>
                </TableHead>
                <tbody>
                    {currentItems.map(({ advertId, title, created, user }) => (
                        <TableRow
                            key={advertId}
                            onClick={() => {
                                selectAdvertId(advertId);
                            }}
                        >
                            <td>{title}</td>
                            <td>{tryFormatDateTime(created)}</td>
                            <td className="text-end">
                                {user.firstName} {user.lastName}
                            </td>
                        </TableRow>
                    ))}
                </tbody>
            </Table>
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={adverts.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                ariaLabel="Annonslistans sidnavigering"
            />
        </Fragment>
    );
};

export default ListActiveUserAdverts;
